import React, { useEffect, useState, useRef } from 'react';

import { DataGrid } from '@mui/x-data-grid';
import { Card } from '@sunwisesoftware/sunwise-ui';
import { debounce, isEqual, isNull } from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { CustomPagination } from 'common/components';
import BulkOperationsCard from 'common/components/BulkOperationsCard';
import Filters from 'common/components/Filters';
import { DEBOUNCE_TIME_500, TABLE_PAGE_SIZE_OPTIONS } from 'common/constants';

import ProductColumns from './ProductColumns';

const ListItems = ({
    canDelete,
    canModify,
    filterData,
    filterItems,
    handleClickBulkItems,
    handleClickDuplicate,
    handleClickPreview,
    handleClickUpdate,
    handleSaveRow,
    isFetching,
    items,
    paginationData,
    setFilterData,
}) => {
    const { t } = useTranslation();
    const [rowCount, setRowCount] = useState(paginationData?.rowCount || 0);
    const [rowModesModel, setRowModesModel] = useState({});
    const [selectedBulkOption, setSelectedBulkOption] = useState('');
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [selectionModel, setSelectionModel] = useState([]);
    const [currentFilterData, setCurrentFilterData] = useState({});

    useEffect(() => {
        if (paginationData)
            setRowCount((prevRowCount) =>
                paginationData?.total_rows !== undefined
                    ? paginationData?.total_rows
                    : prevRowCount,
            );
    }, [paginationData?.total_rows, setRowCount]);

    useEffect(() => {
        if (!isEqual(currentFilterData, filterData)) {
            handleOnFilter(filterData);
            setCurrentFilterData(filterData);
        }
    }, [filterData]);

    const handleOnFilter = useRef(
        debounce((filter) => filterItems(filter), DEBOUNCE_TIME_500),
    ).current;

    const handleOnChangeSearch = (event) => {
        setFilterData({
            ...filterData,
            page: 0,
            searchText: event.target.value,
        });
    };

    const handleOnPageChange = (page) => {
        setFilterData({ ...filterData, page: page });
    };

    const handleOnSelectStatus = (value) => {
        const filter = { ...filterData, page: 0, status: value };
        setFilterData(filter);
    };

    const prefix = t('{{count}} selected product', {
        count: selectionModel.length,
    });

    const pageSizeOptions = TABLE_PAGE_SIZE_OPTIONS;

    const columns = ProductColumns({
        canDelete,
        canModify,
        filterData,
        handleClickBulkItems,
        handleClickDuplicate,
        handleClickUpdate,
        handleClickPreview,
        rowModesModel,
        selectedRowId,
        setRowModesModel,
        setSelectedRowId,
    });
    const getRowHeight = () => 'auto';
    const initialState = {
        pagination: {
            paginationModel: { pageSize: TABLE_PAGE_SIZE_OPTIONS[0] },
        },
    };

    const onPaginationModelChange = (model) =>
        setFilterData((prev) => ({ ...prev, ...model }));
    const onRowEditStart = (params, event) => {
        const id = params.row.id;
        if (isNull(selectedRowId)) setSelectedRowId(id);
        else event.defaultMuiPrevented = true;
        return false;
    };
    const onRowEditStop = (_, event) => {
        event.defaultMuiPrevented = true;
    };
    const onRowModesModelChange = (model) => setRowModesModel(model);
    const onRowSelectionModelChange = (model) => setSelectionModel(model);
    const onSortModelChange = (sortModel) => {
        if (!sortModel[0]) return;
        setFilterData((prev) => ({
            ...prev,
            orderBy: sortModel[0].field.toLocaleLowerCase(),
            sortBy: sortModel[0].sort,
        }));
    };
    const processRowUpdate = (newRow) => {
        handleSaveRow(newRow);
        return newRow;
    };

    const slots = { pagination: CustomPagination };
    const slotProps = { pagination: { pageSizeOptions } };

    return (
        <>
            <Filters
                filterData={filterData}
                handleOnChangeSearch={handleOnChangeSearch}
                handleOnSelectStatus={handleOnSelectStatus}
            />

            <Card>
                <Card.Body>
                    <BulkOperationsCard
                        canDelete={canDelete}
                        filterData={filterData}
                        handleClickBulkItems={handleClickBulkItems}
                        prefix={prefix}
                        selectedBulkOption={selectedBulkOption}
                        selectionModel={selectionModel}
                        setSelectedBulkOption={setSelectedBulkOption}
                        setSelectionModel={setSelectionModel}
                    />
                    <DataGrid
                        checkboxSelection={canDelete}
                        columns={columns}
                        disableColumnMenu
                        disableRowSelectionOnClick
                        editMode="row"
                        getRowHeight={getRowHeight}
                        initialState={initialState}
                        loading={isFetching}
                        onPageChange={handleOnPageChange}
                        onPaginationModelChange={onPaginationModelChange}
                        onRowEditStart={onRowEditStart}
                        onRowEditStop={onRowEditStop}
                        onRowModesModelChange={onRowModesModelChange}
                        onRowSelectionModelChange={onRowSelectionModelChange}
                        onSortModelChange={onSortModelChange}
                        pageSizeOptions={pageSizeOptions}
                        pagination
                        paginationMode="server"
                        processRowUpdate={processRowUpdate}
                        rowCount={rowCount}
                        rowModesModel={rowModesModel}
                        rowSelectionModel={selectionModel}
                        rows={items}
                        slotProps={slotProps}
                        slots={slots}
                        sortingMode="server"
                        sx={{
                            '& .MuiDataGrid-row--editing .MuiDataGrid-cell': {
                                backgroundColor: 'rgba(255, 154, 0, 0.16)',
                            },
                            '& .MuiDataGrid-row--editing:hover': {
                                backgroundColor: 'rgba(255, 154, 0, 0.08)',
                            },
                            '& .MuiInputBase-formControl input': {
                                border: 0,
                                boxShadow: 'none',
                                outline: 'none',
                            },
                        }}
                    />
                </Card.Body>
            </Card>
        </>
    );
};

ListItems.propTypes = {
    canDelete: PropTypes.bool,
    canModify: PropTypes.bool,
    filterData: PropTypes.object,
    filterItems: PropTypes.func,
    handleClickBulkItems: PropTypes.func,
    handleClickDuplicate: PropTypes.func,
    handleClickPreview: PropTypes.func,
    handleClickUpdate: PropTypes.func,
    handleSaveRow: PropTypes.func,
    isFetching: PropTypes.bool,
    items: PropTypes.array,
    paginationData: PropTypes.object,
    setFilterData: PropTypes.func,
};

export default ListItems;
